import { useState } from 'react';
import { EmailAccount, setupHostingerWebhook, deleteHostingerWebhook } from '@/services/api/emailAccounts';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Mail, Pencil, Trash2, Zap, Copy, Check, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface EmailAccountListItemProps {
  account: EmailAccount;
  onEdit: (account: EmailAccount) => void;
  onDelete: (accountId: number) => void;
  isDeleting?: boolean;
}

// The backend has no webhook-status GET, so the toggle state is kept client-side per account.
const WEBHOOK_STATE_KEY = 'hostinger_live_inbox';

type WebhookStateMap = Record<string, { enabled: boolean; webhook_url?: string }>;

const readWebhookState = (): WebhookStateMap => {
  try {
    return JSON.parse(localStorage.getItem(WEBHOOK_STATE_KEY) || '{}');
  } catch {
    return {};
  }
};

const writeWebhookState = (map: WebhookStateMap) => {
  localStorage.setItem(WEBHOOK_STATE_KEY, JSON.stringify(map));
};

export const EmailAccountListItem = ({
  account,
  onEdit,
  onDelete,
  isDeleting = false,
}: EmailAccountListItemProps) => {
  const { toast } = useToast();
  const isHostinger = account.provider === 'hostinger';
  const accountKey = String(account.id ?? '');

  const [webhookInfo, setWebhookInfo] = useState<{ enabled: boolean; webhook_url?: string }>(
    () => readWebhookState()[accountKey] || { enabled: false }
  );
  const [isTogglingWebhook, setIsTogglingWebhook] = useState(false);
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [copied, setCopied] = useState(false);

  const persistWebhookInfo = (info: { enabled: boolean; webhook_url?: string }) => {
    const map = readWebhookState();
    if (info.enabled) {
      map[accountKey] = info;
    } else {
      delete map[accountKey];
    }
    writeWebhookState(map);
    setWebhookInfo(info);
  };

  const handleEnableLiveInbox = async () => {
    if (!account.id) return;
    setIsTogglingWebhook(true);
    try {
      const response = await setupHostingerWebhook(account.id);
      persistWebhookInfo({ enabled: true, webhook_url: response.webhook_url });
      toast({
        title: 'Live inbox enabled',
        description: 'New mail will now be pushed to this mailbox in real time.',
      });
    } catch (error: any) {
      toast({
        title: 'Failed to enable live inbox',
        description: error?.message || 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsTogglingWebhook(false);
    }
  };

  const handleDisableLiveInbox = async () => {
    if (!account.id) return;
    setIsTogglingWebhook(true);
    try {
      await deleteHostingerWebhook(account.id);
      persistWebhookInfo({ enabled: false });
      setShowDisableDialog(false);
      toast({
        title: 'Live inbox disabled',
        description: 'The inbound webhook has been removed. Mail will sync on schedule instead.',
      });
    } catch (error: any) {
      toast({
        title: 'Failed to disable live inbox',
        description: error?.message || 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsTogglingWebhook(false);
    }
  };

  const handleCopyWebhookUrl = async () => {
    if (!webhookInfo.webhook_url) return;
    try {
      await navigator.clipboard.writeText(webhookInfo.webhook_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: 'Copy failed', description: 'Could not copy the webhook URL.', variant: 'destructive' });
    }
  };

  return (
    <div className="p-4 border rounded-lg hover:bg-accent/50 transition-colors">
      <div className="flex items-center justify-between">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="p-2 rounded-full bg-primary/10">
            <Mail className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="font-semibold truncate">{account.email_address}</h4>
              {isHostinger ? (
                <Badge variant="default" className="gap-1">
                  <Zap className="h-3 w-3" />
                  Hostinger
                </Badge>
              ) : (
                <Badge variant="outline">IMAP</Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground">{account.display_name}</p>
            <div className="flex flex-col gap-1 mt-2 text-xs text-muted-foreground">
              {isHostinger ? (
                <span>Hostinger Mail API — server settings managed automatically</span>
              ) : (
                <>
                  <span>IMAP: {account.imap_server}:{account.imap_port}</span>
                  <span>SMTP: {account.smtp_server}:{account.smtp_port}</span>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(account)}
            disabled={isDeleting}
          >
            <Pencil className="h-4 w-4 mr-1" />
            Edit
          </Button>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="sm" disabled={isDeleting}>
                <Trash2 className="h-4 w-4 mr-1" />
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Email Account</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete the account <strong>{account.email_address}</strong>?
                  This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => account.id && onDelete(account.id)}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Live inbox (Hostinger webhook) — one-time setup per Hostinger account */}
      {isHostinger && account.id && (
        <div className="mt-3 pt-3 border-t border-border/50 space-y-2">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor={`live-inbox-${account.id}`} className="text-sm font-medium">
                Live inbox
              </Label>
              <p className="text-xs text-muted-foreground">
                Push new mail instantly via webhook instead of waiting for scheduled syncs.
              </p>
            </div>
            <div className="flex items-center gap-2">
              {isTogglingWebhook && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
              <Switch
                id={`live-inbox-${account.id}`}
                checked={webhookInfo.enabled}
                disabled={isTogglingWebhook}
                onCheckedChange={(checked) => {
                  if (checked) {
                    handleEnableLiveInbox();
                  } else {
                    setShowDisableDialog(true);
                  }
                }}
              />
            </div>
          </div>

          {webhookInfo.enabled && webhookInfo.webhook_url && (
            <div className="flex items-center gap-2 rounded-md bg-muted/40 px-2 py-1.5">
              <code className="flex-1 text-xs text-muted-foreground truncate">
                {webhookInfo.webhook_url}
              </code>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={handleCopyWebhookUrl}
                title="Copy webhook URL"
              >
                {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
              </Button>
            </div>
          )}

          <AlertDialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Disable live inbox?</AlertDialogTitle>
                <AlertDialogDescription>
                  This removes the inbound webhook for <strong>{account.email_address}</strong>.
                  New mail will only arrive on scheduled syncs until you enable it again.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isTogglingWebhook}>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDisableLiveInbox} disabled={isTogglingWebhook}>
                  Disable
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}
    </div>
  );
};
